import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { History, RefreshCw, GitCompare, Clock, User, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Version {
  id: string;
  versionNumber: number;
  createdAt: string;
  createdBy?: string;
  changeType: string;
  changeReason?: string;
  changes?: any;
}

interface VersionHistoryPanelProps {
  versions: Version[];
  isLoading: boolean;
  onRestore: (versionNumber: number) => void;
  onCompare: (version1: number, version2: number) => void;
  currentVersionNumber?: number;
}

const changeTypeColors: Record<string, string> = {
  CREATED: 'bg-green-100 text-green-800',
  UPDATED: 'bg-blue-100 text-blue-800',
  PUBLISHED: 'bg-purple-100 text-purple-800',
  UNPUBLISHED: 'bg-gray-100 text-gray-800',
  ARCHIVED: 'bg-yellow-100 text-yellow-800',
  MODEL_UPDATED: 'bg-indigo-100 text-indigo-800',
  DATA_IMPORTED: 'bg-cyan-100 text-cyan-800',
  GENERATED: 'bg-teal-100 text-teal-800',
  OPTIMIZED: 'bg-pink-100 text-pink-800',
  DUPLICATED: 'bg-orange-100 text-orange-800',
};

const changeTypeLabels: Record<string, string> = {
  CREATED: 'Created',
  UPDATED: 'Updated',
  PUBLISHED: 'Published',
  UNPUBLISHED: 'Unpublished',
  ARCHIVED: 'Archived',
  MODEL_UPDATED: 'Model Updated',
  DATA_IMPORTED: 'Data Imported',
  GENERATED: 'Generated',
  OPTIMIZED: 'Optimized',
  DUPLICATED: 'Duplicated',
};

export function VersionHistoryPanel({
  versions,
  isLoading,
  onRestore,
  onCompare,
  currentVersionNumber,
}: VersionHistoryPanelProps) {
  const [selectedVersion1, setSelectedVersion1] = useState<number | null>(null);
  const [selectedVersion2, setSelectedVersion2] = useState<number | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);

  const handleCompare = () => {
    if (selectedVersion1 !== null && selectedVersion2 !== null) {
      onCompare(selectedVersion1, selectedVersion2);
    }
  };

  const handleVersionClick = (versionNumber: number) => {
    if (selectedVersion1 === null) {
      setSelectedVersion1(versionNumber);
    } else if (selectedVersion2 === null) {
      setSelectedVersion2(versionNumber);
    } else {
      setSelectedVersion1(versionNumber);
      setSelectedVersion2(null);
    }
  };

  const clearSelection = () => {
    setSelectedVersion1(null);
    setSelectedVersion2(null);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Loading version history...</p>
        </div>
      </Card>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <Card>
        <div className="p-6 text-center">
          <History className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">No version history available</p>
          <p className="text-sm text-gray-500 mt-1">
            Versions will be created as changes are made
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
            <Badge variant="default">{versions.length} versions</Badge>
          </div>
          {(selectedVersion1 !== null || selectedVersion2 !== null) && (
            <div className="flex items-center gap-2">
              {selectedVersion1 !== null && selectedVersion2 !== null && (
                <Button onClick={handleCompare} className="flex items-center gap-2">
                  <GitCompare className="w-4 h-4" />
                  Compare v{selectedVersion1} & v{selectedVersion2}
                </Button>
              )}
              <Button variant="secondary" onClick={clearSelection}>
                Clear Selection
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {versions.map((version) => {
          const isSelected =
            version.versionNumber === selectedVersion1 ||
            version.versionNumber === selectedVersion2;
          const isCurrent = version.versionNumber === currentVersionNumber;
          const isExpanded = expandedVersion === version.versionNumber;

          return (
            <div
              key={version.id}
              className={`p-4 hover:bg-gray-50 transition-colors ${
                isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
              } ${isCurrent ? 'bg-green-50' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-gray-900">
                      Version {version.versionNumber}
                    </span>
                    {isCurrent && (
                      <Badge variant="success" className="text-xs">
                        Current
                      </Badge>
                    )}
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        changeTypeColors[version.changeType] || 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {changeTypeLabels[version.changeType] || version.changeType}
                    </span>
                  </div>

                  {version.changeReason && (
                    <div className="flex items-start gap-2 text-sm text-gray-700 mb-2">
                      <FileText className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                      <span>{version.changeReason}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>
                        {formatDistanceToNow(new Date(version.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    {version.createdBy && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span>{version.createdBy}</span>
                      </div>
                    )}
                  </div>

                  {version.changes && isExpanded && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md text-sm">
                      <p className="font-medium text-gray-900 mb-2">Changes in this version:</p>
                      {renderChanges(version.changes)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {version.changes && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setExpandedVersion(isExpanded ? null : version.versionNumber)
                      }
                    >
                      {isExpanded ? 'Hide' : 'Show'} Changes
                    </Button>
                  )}
                  {!isCurrent && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleVersionClick(version.versionNumber)}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onRestore(version.versionNumber)}
                        className="flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Restore
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function renderChanges(changes: any) {
  if (!changes) return null;

  const elements: JSX.Element[] = [];

  // Schedule/Forecast field changes
  if (changes.schedule && Object.keys(changes.schedule).length > 0) {
    elements.push(
      <div key="schedule-changes" className="mb-2">
        <p className="font-medium text-gray-800 mb-1">Schedule Changes:</p>
        <ul className="list-disc list-inside space-y-1">
          {Object.entries(changes.schedule).map(([field, change]: [string, any]) => (
            <li key={field} className="text-gray-700">
              <span className="font-medium">{field}</span>:{' '}
              <span className="line-through text-gray-500">{JSON.stringify(change.old)}</span> →{' '}
              <span className="text-blue-600">{JSON.stringify(change.new)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (changes.forecast && Object.keys(changes.forecast).length > 0) {
    elements.push(
      <div key="forecast-changes" className="mb-2">
        <p className="font-medium text-gray-800 mb-1">Forecast Changes:</p>
        <ul className="list-disc list-inside space-y-1">
          {Object.entries(changes.forecast).map(([field, change]: [string, any]) => (
            <li key={field} className="text-gray-700">
              <span className="font-medium">{field}</span>:{' '}
              <span className="line-through text-gray-500">
                {JSON.stringify(change.old, null, 2)}
              </span>{' '}
              → <span className="text-blue-600">{JSON.stringify(change.new, null, 2)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Shift changes
  if (changes.shifts) {
    const { added, removed, modified } = changes.shifts;
    if (added.length > 0 || removed.length > 0 || modified.length > 0) {
      elements.push(
        <div key="shift-changes" className="mb-2">
          <p className="font-medium text-gray-800 mb-1">Shift Changes:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            {added.length > 0 && <li>{added.length} shift(s) added</li>}
            {removed.length > 0 && <li>{removed.length} shift(s) removed</li>}
            {modified.length > 0 && <li>{modified.length} shift(s) modified</li>}
          </ul>
        </div>
      );
    }
  }

  // Data point changes
  if (changes.dataPoints) {
    const { added, removed, modified, totalOld, totalNew } = changes.dataPoints;
    elements.push(
      <div key="datapoint-changes" className="mb-2">
        <p className="font-medium text-gray-800 mb-1">Data Point Changes:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700">
          <li>Total: {totalOld} → {totalNew}</li>
          {added > 0 && <li className="text-green-600">{added} added</li>}
          {removed > 0 && <li className="text-red-600">{removed} removed</li>}
          {modified > 0 && <li className="text-blue-600">{modified} modified</li>}
        </ul>
      </div>
    );
  }

  return elements.length > 0 ? <>{elements}</> : <p className="text-gray-500">No changes recorded</p>;
}
